/* eslint-disable @typescript-eslint/no-empty-interface */
import { applyMixins, PDFManagerFactory, WebViewerWrapper } from 'pdftron';
import { DocumentTypes, InputAnnotation, InspectionSettings, PDFTronTools, Polygons } from 'types';
import Annotation from './Annotation';
import Crossout from './AnnotationTools/Crossout';
import GraphicZone from './AnnotationTools/GraphicZone';
import ColorPickerZone from './AnnotationTools/ColorPickerZone';
import MarqueeZone from './AnnotationTools/MarqueeZone';
import TextZone from './AnnotationTools/TextZone';
import Difference from './Difference';
import InspectedAnnotation from './InspectedAnnotation';
import Tools from './Tools';
import CropZone from './AnnotationTools/CropZone';
import ManualAlignZone from './AnnotationTools/ManualAlignZone';
import PolygonZone from './AnnotationTools/PolygonZone';
import MarkupZone from './AnnotationTools/MarkupZone';
import ResyncZone from './AnnotationTools/ResyncZone';
import MergeGraphicsZone from './AnnotationTools/MergeGraphicsZone';
import store from 'store/store';
import {
  getAnnotations,
  getDifferences,
  getDisplayedDifferences,
  getDisplayedRepeats,
  getInspectionSettings,
  getRepeats,
} from 'store';
import PDFAnnotationManager from 'components/PDFViewer/PDFAnnotationManager';
import { isInspectionUrl, isReportsUrl, isResultUrl } from 'utils/location';

class PDFDocManager {
  source: WebViewerWrapper | null = null;

  target: WebViewerWrapper | null = null;

  private rotationListenerCleanups: Map<DocumentTypes, () => void> = new Map();

  setInstance(instance: WebViewerWrapper | null, documentType: DocumentTypes) {
    const previousCleanup = this.rotationListenerCleanups.get(documentType);
    if (previousCleanup) {
      previousCleanup();
      this.rotationListenerCleanups.delete(documentType);
    }

    if (documentType === DocumentTypes.source) {
      this.source = instance;
    } else if (documentType === DocumentTypes.target) {
      this.target = instance;
    }

    if (!instance) return;

    // Redraw on rotation so labels drawn outside the annotation's rect don't leave stale pixels.
    const { documentViewer } = instance.instance.Core;
    const rotationHandler = (_rotation: unknown, rotatedPageNumber?: number) => {
      if (rotatedPageNumber === undefined) return;
      const annotManager = instance.annotationManager;
      annotManager.drawAnnotations({ pageNumber: rotatedPageNumber, majorRedraw: true });
      // Reselect on the rotated page so the annotation popup (e.g. trash-can) re-renders
      // against the new transform; PDFTron caches its screen position otherwise.
      const selectedOnRotatedPage = annotManager
        .getSelectedAnnotations()
        .filter((annot) => annot.PageNumber === rotatedPageNumber);
      if (selectedOnRotatedPage.length > 0) {
        annotManager.deselectAnnotations(selectedOnRotatedPage);
        annotManager.selectAnnotations(selectedOnRotatedPage);
      }
    };
    documentViewer.addEventListener('rotationUpdated', rotationHandler);
    this.rotationListenerCleanups.set(documentType, () => {
      documentViewer.removeEventListener('rotationUpdated', rotationHandler);
    });
  }

  getInstance(documentType: DocumentTypes): WebViewerWrapper | null {
    if (documentType === DocumentTypes.source) {
      return this.source;
    }
    if (documentType === DocumentTypes.target) {
      return this.target;
    }
    return null;
  }

  getOtherInstance(instance: WebViewerWrapper | null) {
    if (instance && instance.documentType === DocumentTypes.source) {
      return this.getInstance(DocumentTypes.target);
    }
    return this.getInstance(DocumentTypes.source);
  }

  drawAnnotations() {
    const state = store.getState();
    const allDifferences = getDifferences(state);
    const displayedDifferences = getDisplayedDifferences(state);
    const allRepeats = getRepeats(state);
    const displayedRepeats = getDisplayedRepeats(state);

    const polygons: Polygons = state.inspection.polygons;
    if (polygons && !isResultUrl()) {
      this.drawPolygons(DocumentTypes.source, polygons.source?.points);
      this.drawPolygons(DocumentTypes.target, polygons.target?.points);
    }

    const annotationsList: { source: InputAnnotation[]; target: InputAnnotation[] } = getAnnotations(state);
    const inspectionSettings: InspectionSettings = getInspectionSettings(state);

    const viewer = this.getInstance(DocumentTypes.source);
    const targetViewer = this.getInstance(DocumentTypes.target);

    if (
      (!inspectionSettings.singleFile && (!viewer || !targetViewer)) ||
      (inspectionSettings.singleFile && !targetViewer)
    )
      return;
    if (viewer) {
      if (viewer.instance?.UI?.iframeWindow?.frameElement) {
        const iframeEl = viewer.instance.UI.iframeWindow.frameElement;
        iframeEl.classList.add(`${DocumentTypes.source}-loaded`); // for e2e tests to know the annotations are loaded
      }
      const annotationManager = viewer.docViewer.getAnnotationManager();
      PDFAnnotationManager.setInstance(DocumentTypes.source, annotationManager);
      // getting a 1-totalPages sequence
      const pages = Array.from({ length: viewer?.getTotalPages() }, (_, index) => index);

      // repainting pages to show all annotations properly
      viewer.docViewer.recalculateLayout(pages);
    }

    if (targetViewer) {
      if (targetViewer.instance?.UI?.iframeWindow?.frameElement) {
        const iframeEl = targetViewer.instance.UI.iframeWindow.frameElement;
        iframeEl.classList.add(`${DocumentTypes.target}-loaded`); // for e2e tests to know the annotations are loaded
      }

      const targetAnnotationManager = targetViewer?.docViewer.getAnnotationManager();
      if (targetAnnotationManager) {
        PDFAnnotationManager.setInstance(DocumentTypes.target, targetAnnotationManager);
      }

      // getting a 1-totalPages sequence
      const targetPages = Array.from({ length: targetViewer.getTotalPages() }, (_, index) => index);

      // repainting pages to show all annotations properly
      targetViewer.docViewer.recalculateLayout(targetPages);
    }

    // paint annotations here
    // at this point we are ready to draw annotations on the document
    // @pdftron-refactor - all this drawing here has to be reviewed - we may be doing a lot of unnecesary stuff
    if (isInspectionUrl()) {
      if (isResultUrl() || isReportsUrl()) {
        PDFAnnotationManager.deleteAllInputAnnotations(); // remove markups (zones/underlines,etc) so they dont appear in results
        // displayedDifferences is used here because we dont need to account for discarded differences
        PDFAnnotationManager.changeDocumentsAnnotationVisibility(displayedDifferences, displayedRepeats); // toggle visibility of differences
        PDFManagerFactory.getPDFDocManager()?.changeInspectedAnnotationVisibility(true);
      } else {
        // allDifferences is used here because at this point we have to hide everything to let zones and crossout to be the only ones visible
        PDFAnnotationManager.changeDocumentsAnnotationVisibility(allDifferences, allRepeats, false, false); // toggle visibility of differences
        PDFManagerFactory.getPDFDocManager()?.changeInspectedAnnotationVisibility(false);

        const sourceCrossouts = annotationsList[DocumentTypes.source].filter(
          (annotation: InputAnnotation) => annotation.usedTool === PDFTronTools.CROSSOUT,
        );
        const targetCrossouts = annotationsList[DocumentTypes.target].filter(
          (annotation: InputAnnotation) => annotation.usedTool === PDFTronTools.CROSSOUT,
        );
        PDFManagerFactory.getPDFDocManager()?.reloadCrossouts(sourceCrossouts, targetCrossouts);

        const sourceCropZones = annotationsList[DocumentTypes.source].filter(
          (annotation: InputAnnotation) => annotation.usedTool === PDFTronTools.CROP,
        );
        const targetCropZones = annotationsList[DocumentTypes.target].filter(
          (annotation: InputAnnotation) => annotation.usedTool === PDFTronTools.CROP,
        );
        PDFManagerFactory.getPDFDocManager()?.reloadCropZones(sourceCropZones, targetCropZones);

        const sourcePolygonCropZones = annotationsList[DocumentTypes.source].filter(
          (annotation: InputAnnotation) => annotation.usedTool === PDFTronTools.POLYGON,
        );
        const targetPolygonCropZones = annotationsList[DocumentTypes.target].filter(
          (annotation: InputAnnotation) => annotation.usedTool === PDFTronTools.POLYGON,
        );
        PDFManagerFactory.getPDFDocManager()?.reloadPolygonZones(sourcePolygonCropZones, targetPolygonCropZones);

        // Reload manual align zones (async — overlay images are fetched in the background)
        const sourceManualAlignZones = annotationsList[DocumentTypes.source].filter(
          (annotation: InputAnnotation) => annotation.manualAlignZone,
        );
        const targetManualAlignZones = annotationsList[DocumentTypes.target].filter(
          (annotation: InputAnnotation) => annotation.manualAlignZone,
        );
        if (sourceManualAlignZones.length > 0 || targetManualAlignZones.length > 0) {
          PDFManagerFactory.getPDFDocManager()?.reloadManualAlignZones(sourceManualAlignZones, targetManualAlignZones);
        }

        PDFAnnotationManager.drawZones();

        const sourceMarqueeZoneAnnotationList = annotationsList[DocumentTypes.source].filter(
          (annotation: InputAnnotation) => annotation.usedTool === PDFTronTools.MARQUEE_ZONE,
        );
        const targetMarqueeZoneAnnotationList = annotationsList[DocumentTypes.target].filter(
          (annotation: InputAnnotation) => annotation.usedTool === PDFTronTools.MARQUEE_ZONE,
        );
        PDFManagerFactory?.getPDFDocManager()?.reloadMarqueeZones(
          sourceMarqueeZoneAnnotationList,
          targetMarqueeZoneAnnotationList,
        );

        const targetSamplingPoints = annotationsList[DocumentTypes.target].filter(
          (annotation: InputAnnotation) => annotation.usedTool === PDFTronTools.COLOR_PICKER,
        );
        if (targetSamplingPoints.length > 0) {
          PDFManagerFactory.getPDFDocManager()?.reloadSamplingPoints(targetSamplingPoints);
        }
      }
    }
  }

  redrawDifferenceHighlights() {
    // get both instances
    const sourceInstance = this.getInstance(DocumentTypes.source);
    const targetInstance = this.getInstance(DocumentTypes.target);
    // update and refresh view
    if (sourceInstance) {
      sourceInstance.instance.Core.documentViewer.refreshAll();
      sourceInstance.instance.Core.documentViewer.updateView();
    }
    if (targetInstance) {
      targetInstance.instance.Core.documentViewer.refreshAll();
      targetInstance.instance.Core.documentViewer.updateView();
    }
  }
}

interface PDFDocManager
  extends
    TextZone,
    MarqueeZone,
    Difference,
    Annotation,
    GraphicZone,
    ColorPickerZone,
    Tools,
    Crossout,
    CropZone,
    ManualAlignZone,
    InspectedAnnotation,
    PolygonZone,
    ResyncZone,
    MarkupZone,
    MergeGraphicsZone {}

applyMixins(PDFDocManager, [
  TextZone,
  MarqueeZone,
  Difference,
  Annotation,
  GraphicZone,
  ColorPickerZone,
  Tools,
  Crossout,
  CropZone,
  ManualAlignZone,
  InspectedAnnotation,
  PolygonZone,
  ResyncZone,
  MarkupZone,
  MergeGraphicsZone,
]);

export default PDFDocManager;
